/**
 * 全局错误处理工具
 * @author AI Assistant
 * @date 2024-01-01
 */

import { ElMessage, ElNotification } from 'element-plus'
import type { App } from 'vue'

/**
 * 错误类型枚举
 */
export enum ErrorType {
  NETWORK = 'network',
  BUSINESS = 'business',
  SYSTEM = 'system',
  VALIDATION = 'validation'
}

/**
 * 错误信息接口
 */
export interface IErrorInfo {
  type: ErrorType
  code?: string | number
  message: string
  details?: any
  timestamp?: number
}

/**
 * 错误处理器类
 */
class ErrorHandler {
  private isDev = import.meta.env.DEV

  /**
   * 处理错误
   * @param error 错误信息
   */
  handle(error: IErrorInfo | Error | string): void {
    let errorInfo: IErrorInfo

    if (typeof error === 'string') {
      errorInfo = {
        type: ErrorType.SYSTEM,
        message: error,
        timestamp: Date.now()
      }
    } else if (error instanceof Error) {
      errorInfo = {
        type: ErrorType.SYSTEM,
        message: error.message,
        details: error.stack,
        timestamp: Date.now()
      }
    } else {
      errorInfo = {
        ...error,
        timestamp: error.timestamp || Date.now()
      }
    }

    // 控制台输出（开发环境）
    if (this.isDev) {
      console.error('[Error Handler]:', errorInfo)
    }

    // 用户提示
    this.showUserMessage(errorInfo)

    // 错误上报（生产环境）
    if (!this.isDev) {
      this.reportError(errorInfo)
    }
  }

  /**
   * 显示用户提示
   * @param error 错误信息
   */
  private showUserMessage(error: IErrorInfo): void {
    const { type, message } = error

    switch (type) {
      case ErrorType.NETWORK:
        ElMessage.error('网络连接异常，请检查网络设置')
        break
      case ErrorType.BUSINESS:
        ElMessage.warning(message || '业务处理失败')
        break
      case ErrorType.VALIDATION:
        ElMessage.warning(message || '数据验证失败')
        break
      case ErrorType.SYSTEM:
      default:
        // const displayMessage =
        //   (error.details && (error.details as any).error?.stack) ||
        //   message ||
        //   '系统异常，请联系管理员'

        // ElNotification.error({
        //   title: '系统错误',
        //   message: displayMessage,
        //   duration: 0 // 临时设成不自动消失方便看
        // })
        // break
        if (!this.isDev) {
          ElNotification.error({
            title: '系统错误',
            message: message || '系统异常，请联系管理员',
            duration: 5000
          })
        } else {
          ElMessage.error('系统异常，请稍后重试')
        }
        break
    }
  }

  /**
   * 错误上报
   * @param error 错误信息
   */
  private reportError(error: IErrorInfo): void {
    // TODO: 实现错误上报逻辑
    // 可以发送到错误监控服务，如 Sentry、Bugsnag 等
    console.log('Error reported:', error)
  }
}

// 创建全局错误处理器实例
const errorHandler = new ErrorHandler()

/**
 * 全局错误处理函数
 * @param error 错误信息
 */
export const handleError = (error: IErrorInfo | Error | string): void => {
  errorHandler.handle(error)
}

/**
 * Vue 应用错误处理器设置
 * @param app Vue 应用实例
 */
export const setupErrorHandler = (app: App): void => {
  // Vue 错误处理
  app.config.errorHandler = (err, instance, info) => {
    const error = err as Error & { message?: string }
    console.error('Vue Error:', error, info)
    handleError({
      type: ErrorType.SYSTEM,
      message: error?.message || 'Vue 组件错误',
      details: { error, info, instance },
      timestamp: Date.now()
    })
  }

  // 未捕获的 Promise 错误
  window.addEventListener('unhandledrejection', (event) => {
    const reason: any = event.reason

    const message = typeof reason === 'string' ? reason : reason?.message

    const isAxiosLikeError = !!(reason?.isAxiosError || reason?.config || reason?.response)

    const isCanceledError =
      reason?.code === 'ERR_CANCELED' ||
      reason?.name === 'CanceledError' ||
      reason?.name === 'AbortError' ||
      (typeof message === 'string' && /cancelled|canceled|aborted/i.test(message))

    if (isAxiosLikeError || isCanceledError) {
      console.warn('Ignored unhandled promise rejection from request:', reason)
      event.preventDefault()
      return
    }

    if (import.meta.env.DEV) {
      console.error('Unhandled Promise Rejection:', reason)
    } else {
      console.error('Unhandled Promise Rejection:', message || reason)
    }
    event.preventDefault()
  })

  // 全局 JavaScript 错误
  window.addEventListener('error', (event) => {
    if (typeof event.message === 'string' && event.message.includes('ResizeObserver')) {
      return
    }
    console.error('Global Error:', event.error)
    handleError({
      type: ErrorType.SYSTEM,
      message: event.message || '全局脚本错误',
      details: {
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno,
        error: event.error
      },
      timestamp: Date.now()
    })
  })
}

// 导出错误处理器
export { ErrorHandler }
export default errorHandler
